import React from "react";
import { formatCurrency, formatDate, formatVal, isExpenseLinked } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { ReviewAlert } from "../ReviewBadge";
import {
  ExternalLink, Building2, CreditCard, CheckCircle2, RotateCcw, Pencil,
  Phone, ClipboardCheck, CheckCheck, Copy, ChevronRight,
} from "lucide-react";

const ensureHttp = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);
const telHref = (s) => {
  const d = String(s).replace(/\D/g, "");
  return d.length === 10 ? `+1${d}` : `+${d}`;
};

/** Tooltip text describing where a duplicate transaction was detected. */
const dupScopeTitle = (dup) => {
  if (!dup) return "";
  if (dup.scope === "within") return "Duplicate within this bill";
  if (dup.scope === "both") return "Duplicate within this bill and shared with another bill";
  return "Also appears in another bill";
};

const PHONE_RE = /(\(?\d{3}\)?[\s.\-]?\d{3}[\s.\-]?\d{4})/g;
// Render a string, turning any phone numbers into tel: links (keeps surrounding text like "or")
const renderPhones = (text) =>
  String(text).split(PHONE_RE).map((part, i) =>
    new RegExp(`^${PHONE_RE.source}$`).test(part) ? (
      <a key={`ph-${i}-${part}`} href={`tel:${telHref(part)}`} className="text-primary hover:underline" data-testid="payment-phone-link">{part}</a>
    ) : (
      <span key={`tx-${i}-${part}`}>{part}</span>
    )
  );

const Field = ({ label, value, mono, edited, flagged }) => (
  <div className="flex flex-col gap-0.5 py-2 border-b border-border last:border-0">
    <dt className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
      {label}
      {edited && <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 text-[10px] font-medium normal-case">edited</span>}
    </dt>
    <dd className={`text-sm ${mono ? "font-mono" : ""} ${flagged ? "text-amber-700" : ""}`}>{value}</dd>
  </div>
);

const ExpenseControls = ({ bill, isWriter, expenseLoading, onRequestPush }) => {
  if (isExpenseLinked(bill.expense_post)) {
    return (
      <>
        <Badge variant="outline" className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="detail-expense-logged-badge" title={bill.expense_post?.expense_id ? `Expense ${bill.expense_post.expense_id}` : "Logged to Expenses"}>
          <CheckCheck className="h-3 w-3" /> Logged
        </Badge>
        {isWriter && (
          <Button size="sm" variant="ghost" className="gap-1 h-7 text-muted-foreground" data-testid="detail-repush-expense-button" disabled={expenseLoading} onClick={() => onRequestPush(bill)} title="Push to Expenses again">
            <Building2 className="h-3.5 w-3.5" /> Push again
          </Button>
        )}
      </>
    );
  }
  if (isWriter) {
    return (
      <Button size="sm" variant="outline" className="gap-1 h-7" data-testid="detail-push-expense-button" disabled={expenseLoading} onClick={() => onRequestPush(bill)} title="Push to Expenses app">
        <Building2 className="h-3.5 w-3.5" /> Push to Expenses
      </Button>
    );
  }
  return null;
};

const ReviewedControls = ({ bill, isWriter, onToggleReviewed }) => {
  if (bill.review?.reviewed) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700" data-testid="detail-reviewed-pill">
        <CheckCircle2 className="h-3.5 w-3.5" /> Reviewed
        {isWriter && (
          <button type="button" className="ml-1 underline decoration-dotted hover:text-emerald-900" data-testid="detail-unreview-button" onClick={() => onToggleReviewed(bill)}>undo</button>
        )}
      </span>
    );
  }
  if (isWriter && bill.review?.needs_review) {
    return (
      <Button size="sm" variant="outline" className="gap-1 h-7 shrink-0" data-testid="detail-mark-reviewed-button" onClick={() => onToggleReviewed(bill)}>
        <ClipboardCheck className="h-3.5 w-3.5" /> Mark reviewed
      </Button>
    );
  }
  return null;
};

const PaymentControls = ({ bill, isWriter, expenseLoading, onRequestPush, onUndoPay, onMarkPaidOpen }) => {
  if (bill.payment_status === "paid") {
    return (
      <div className="flex items-center gap-2 shrink-0">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
          <CheckCircle2 className="h-3.5 w-3.5" /> Paid
        </span>
        <ExpenseControls bill={bill} isWriter={isWriter} expenseLoading={expenseLoading} onRequestPush={onRequestPush} />
        {isWriter && (
          <Button size="sm" variant="ghost" className="gap-1 h-7" data-testid="detail-unpay-button" onClick={() => onUndoPay(bill)}>
            <RotateCcw className="h-3.5 w-3.5" /> Undo
          </Button>
        )}
      </div>
    );
  }
  if (isWriter) {
    return (
      <Button size="sm" variant="outline" className="gap-1 h-7 shrink-0" data-testid="detail-markpaid-button" onClick={() => onMarkPaidOpen(bill)}>
        <CreditCard className="h-3.5 w-3.5" /> Mark paid
      </Button>
    );
  }
  return null;
};

const PossibleDuplicates = ({ bill, onNavigate }) => {
  const dups = bill.possible_duplicates || [];
  if (dups.length === 0) return null;
  return (
    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="detail-possible-duplicates">
      <div className="flex items-center gap-2 text-xs font-semibold text-amber-800">
        <Copy className="h-3.5 w-3.5 shrink-0" />
        {dups.length} possible duplicate{dups.length > 1 ? "s" : ""} found
      </div>
      <p className="mt-1 text-xs text-amber-700">This bill may already exist. Review before paying to avoid double-payment.</p>
      <div className="mt-2 space-y-1.5">
        {dups.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => onNavigate(`/documents/${d.document_id}`)}
            className="w-full flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-white px-2.5 py-2 text-left transition-colors hover:bg-amber-100/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
            data-testid="possible-duplicate-row"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{formatVal(d.issuing_authority)}</div>
              <div className="text-xs text-muted-foreground truncate">
                {(d.match_reasons || []).join(" · ")}
                {d.due_date ? ` · due ${formatDate(d.due_date)}` : ""}
                {d.payment_status === "paid" ? " · Paid" : ""}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm font-semibold tabular-nums">{formatCurrency(d.total_amount_due, d.currency)}</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const has = (bill, field) => (bill.edited_fields || []).includes(field);
const flaggedOn = (bill, field) => (bill.review?.issues || []).some((i) => i.field === field);

const BillFields = ({ bill: b }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
    <dl>
      <Field label="Document type" value={formatVal(b.document_type)} edited={has(b, "document_type")} />
      <Field label="Account ID" value={formatVal(b.account_id)} mono edited={has(b, "account_id")} />
      <Field label="Invoice #" value={formatVal(b.invoice_number)} mono edited={has(b, "invoice_number")} />
      <Field label="License plate" value={`${formatVal(b.license_plate)} ${b.plate_state ? `(${b.plate_state})` : ""}`} edited={has(b, "license_plate") || has(b, "plate_state")} flagged={flaggedOn(b, "license_plate")} />
      <Field label="Owner" value={formatVal(b.owner_name)} edited={has(b, "owner_name")} />
      <Field label="Bill date" value={formatDate(b.bill_date)} edited={has(b, "bill_date")} />
    </dl>
    <dl>
      <Field label="Due date" value={formatDate(b.due_date)} edited={has(b, "due_date")} flagged={flaggedOn(b, "due_date")} />
      <Field label="Toll amount" value={formatCurrency(b.toll_amount, b.currency)} edited={has(b, "toll_amount")} />
      <Field label="Processing fee" value={formatCurrency(b.processing_fee, b.currency)} edited={has(b, "processing_fee")} />
      <Field label="Late fees" value={formatCurrency(b.late_fees, b.currency)} edited={has(b, "late_fees")} />
      <Field label="Previous balance" value={formatCurrency(b.previous_balance, b.currency)} edited={has(b, "previous_balance")} />
      <Field label="Total due" value={<span className="font-semibold">{formatCurrency(b.total_amount_due, b.currency)}</span>} edited={has(b, "total_amount_due")} flagged={flaggedOn(b, "total_amount_due")} />
    </dl>
  </div>
);

const TxnTable = ({ bill: b, onNavigate }) => {
  const txns = b.transactions || [];
  if (txns.length === 0) return null;
  return (
    <div className="mt-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Toll transactions ({txns.length})</div>
      <div className="overflow-x-auto rounded-lg border border-border">
        <Table data-testid="document-line-items-table">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Time</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Rented by</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {txns.map((t, i) => {
              const td = t.date && String(t.date).length >= 10 ? String(t.date).slice(0, 10) : t.date;
              const rental = (b.rental_lookups || []).find((r) => r.date === td);
              const dup = (b.review?.duplicate_transactions || []).find((d) => d.index === i);
              const tnDup = (b.review?.txn_number_dupes || []).find((d) => d.index === i);
              const dupTitle = dupScopeTitle(dup);
              return (
                <TableRow key={`${t.transaction_number || ""}-${t.date || ""}-${t.time || ""}-${t.amount || ""}-${i}`} className={dup ? "bg-amber-50/70" : ""} data-testid={dup ? "duplicate-txn-row" : undefined}>
                  <TableCell className="text-sm">
                    <div className="flex flex-col gap-1">
                      <span className="inline-flex items-center gap-1.5">
                        {dup && (
                          <span title={dupTitle} className="inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                            Duplicate
                          </span>
                        )}
                        {tnDup && (
                          <span title="Same transaction number appears more than once" className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700" data-testid="duplicate-txn-number-badge">
                            Dup txn #
                          </span>
                        )}
                        {formatDate(t.date)}
                      </span>
                      {dup?.other_bills_info?.length > 0 && (
                        <span className="text-[11px] text-muted-foreground">
                          Also in:{" "}
                          {dup.other_bills_info.map((o, oi) => (
                            <React.Fragment key={o.bill_id}>
                              {oi > 0 && ", "}
                              {o.document_id ? (
                                <button
                                  type="button"
                                  onClick={() => onNavigate && onNavigate(`/documents/${o.document_id}`)}
                                  className="text-primary hover:underline font-medium"
                                  data-testid="duplicate-other-bill-link"
                                  title={`Go to ${o.label}`}
                                >
                                  {o.label}
                                </button>
                              ) : (
                                <span className="font-medium">{o.label}</span>
                              )}
                            </React.Fragment>
                          ))}
                        </span>
                      )}
                      {tnDup?.other_bills_info?.length > 0 && (
                        <span className="text-[11px] text-muted-foreground">
                          Same txn # in:{" "}
                          {tnDup.other_bills_info.map((o, oi) => (
                            <React.Fragment key={`tn-${o.bill_id}`}>
                              {oi > 0 && ", "}
                              {o.document_id ? (
                                <button
                                  type="button"
                                  onClick={() => onNavigate && onNavigate(`/documents/${o.document_id}`)}
                                  className="text-primary hover:underline font-medium"
                                  data-testid="duplicate-txn-number-link"
                                  title={`Go to ${o.label}`}
                                >
                                  {o.label}
                                </button>
                              ) : (
                                <span className="font-medium">{o.label}</span>
                              )}
                            </React.Fragment>
                          ))}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{formatVal(t.time)}</TableCell>
                  <TableCell className="text-sm">{formatVal(t.location)}</TableCell>
                  <TableCell className="text-sm">{rental ? rental.customer_name : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{formatCurrency(t.amount, b.currency)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const PaymentInfo = ({ bill: b }) => {
  if (!(b.payment_url || b.payment_phone || b.payment_address)) return null;
  return (
    <div className="mt-4 rounded-lg bg-secondary p-3 text-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Payment</div>
      {b.payment_url && (
        <div className="truncate">
          <a href={ensureHttp(b.payment_url)} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1" data-testid="payment-url-link">
            {b.payment_url}<ExternalLink className="h-3 w-3 shrink-0" />
          </a>
        </div>
      )}
      {b.payment_phone && (
        <div className="inline-flex items-center gap-1" data-testid="payment-phone">
          <Phone className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span>{renderPhones(b.payment_phone)}</span>
        </div>
      )}
      {b.payment_address && <div className="text-muted-foreground">{b.payment_address}</div>}
    </div>
  );
};

/** Compact bill-level indicators: shows when this bill shares an invoice number or a
 * transaction number with another bill. Informational only (navigation is via the
 * possible-duplicates banner / per-row links). */
const DuplicateSummaryBadges = ({ bill: b }) => {
  const invoiceDup = (b.possible_duplicates || []).some((d) => (d.match_reasons || []).includes("Same invoice #"));
  const tnDupes = b.review?.txn_number_dupes || [];
  const txnNumberDupCross = tnDupes.some((d) => (d.other_bills_info || []).length > 0);
  const txnNumberDupWithin = tnDupes.some((d) => (d.other_bills_info || []).length === 0);
  if (!invoiceDup && !txnNumberDupCross && !txnNumberDupWithin) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="duplicate-summary-badges">
      {invoiceDup && (
        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800" data-testid="duplicate-invoice-badge">
          <Copy className="h-3 w-3" /> Duplicate invoice #
        </span>
      )}
      {(txnNumberDupCross || txnNumberDupWithin) && (
        <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700" data-testid="duplicate-txn-number-summary-badge">
          <Copy className="h-3 w-3" /> Duplicate transaction #{txnNumberDupCross ? " (across bills)" : ""}
        </span>
      )}
    </div>
  );
};

/** A single extracted bill: header actions, review/duplicate banners, fields, transactions and payment info. */
export const BillCard = ({ bill: b, idx, billsCount, isWriter, expenseLoading, handlers }) => {
  const { onEdit, onToggleReviewed, onMarkPaidOpen, onUndoPay, onRequestPush, onNavigate } = handlers;
  return (
    <Card data-testid="document-fields-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate">{formatVal(b.issuing_authority)}</span>
            {billsCount > 1 && <span className="text-xs text-muted-foreground">#{idx + 1}</span>}
          </CardTitle>
          <div className="flex items-center gap-2 shrink-0">
            {isWriter && (
              <Button size="sm" variant="outline" className="gap-1 h-7" data-testid="detail-edit-button" onClick={() => onEdit(b)}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            )}
            <ReviewedControls bill={b} isWriter={isWriter} onToggleReviewed={onToggleReviewed} />
            <PaymentControls bill={b} isWriter={isWriter} expenseLoading={expenseLoading} onRequestPush={onRequestPush} onUndoPay={onUndoPay} onMarkPaidOpen={onMarkPaidOpen} />
          </div>
        </div>
        {b.payment_status === "paid" && (
          <div className="text-xs text-muted-foreground mt-1">
            Confirmation: <span className="font-mono">{b.confirmation_number || "—"}</span> · Paid {formatDate(b.paid_at)}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <DuplicateSummaryBadges bill={b} />
        {b.review?.needs_review && (
          <div className="mb-4"><ReviewAlert review={b.review} /></div>
        )}
        {b.review?.reviewed && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="detail-reviewed-note">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            <span>Reviewed{b.review.reviewed_by ? ` by ${b.review.reviewed_by}` : ""}{b.review.reviewed_at ? ` on ${formatDate(b.review.reviewed_at)}` : ""}.</span>
          </div>
        )}
        <PossibleDuplicates bill={b} onNavigate={onNavigate} />
        <BillFields bill={b} />
        <TxnTable bill={b} onNavigate={onNavigate} />
        <PaymentInfo bill={b} />
      </CardContent>
    </Card>
  );
};
